/* eslint-disable react/jsx-props-no-spreading */
import React from 'react';

import cx from 'classnames';
import AppleSignin from 'react-apple-signin-auth';
import { useTranslation } from 'react-i18next';

import { postMessageToMobileApp } from 'components/app/helpers';
import { APPLE_OAUTH_CLIENT_ID, IS_RN_WEBVIEW } from 'constants/constants';
import { OAUTH_PROVIDERS, POST_MESSAGE_TO_MOBILE_EVENTS } from 'constants/enums';
import { isEmpty } from 'helpers/ramda';
import { noop } from 'helpers/utils';
import useTheme from 'hooks/useTheme';
import Box from 'UIKit/Box';
import DeltaButton from 'UIKit/DeltaButton';

import { AppleButtonUIProps, OAuthButtonProps } from './interface';
import classes from './oauth.module.scss';
import OAuthIconWrapper from './OAuthIconWrapper';

const AppleButtonUI = ({
  handleClick = noop,
  provider,
  errorMessage = '',
  disableButton = false,
  loading = false,
  onClickAppleButton = noop,
}: AppleButtonUIProps) => {
  const { isDarkTheme } = useTheme();
  const { t } = useTranslation(['auth']);

  const handleAppleBtnClick = e => {
    handleClick(e);
    if (onClickAppleButton) {
      onClickAppleButton(e);
    }
  };

  return (
    <Box className={classes.buttonWrapper}>
      <DeltaButton
        data-testid="AppleSignInButton"
        fullWidth
        onClick={handleAppleBtnClick}
        onKeyDown={noop}
        className={cx(classes.signInWithAppleBtn, classes.bgDark, classes.bgDarkHover, {
          [cx(classes.bgLight, classes.bgLightHover, classes.loaderColor)]: isDarkTheme,
        })}
        disabled={disableButton}
        loading={loading && provider === OAUTH_PROVIDERS.APPLE}
        wrapperClassName={classes.appleBtnContentWrapper}
      >
        <OAuthIconWrapper
          color={isDarkTheme ? 'dark' : 'light'}
          provider={OAUTH_PROVIDERS.APPLE}
        />
        <div
          className={cx(classes.buttonLabel, {
            [classes.buttonLabelDark]: isDarkTheme,
          })}
        >
          {t('auth:signInWithApple')}
        </div>
      </DeltaButton>
      <Box
        renderWhen={!isEmpty(errorMessage) && provider === OAUTH_PROVIDERS.APPLE}
        className={classes.errorMessage}
      >
        {errorMessage}
      </Box>
    </Box>
  );
};

AppleButtonUI.displayName = 'AppleButtonUI';

const AppleButton = ({
  disableButton = false,
  loading = false,
  provider,
  errorMessage = '',
  onClickOAuthButton,
  handleOAuthSignInSuccess,
}: OAuthButtonProps) => {
  const redirectURI = window.location.origin + window.location.pathname;

  const handleSuccess = response => {
    handleOAuthSignInSuccess({
      accessToken: response?.authorization?.code,
      provider: OAUTH_PROVIDERS.APPLE,
      redirectURI,
      idToken: response?.authorization?.id_token,
    });
  };

  const handleClick = e => {
    e.preventDefault();
    onClickOAuthButton(OAUTH_PROVIDERS.APPLE);
    if (IS_RN_WEBVIEW) {
      postMessageToMobileApp(POST_MESSAGE_TO_MOBILE_EVENTS.OAUTH_BUTTON_CLICKED, {
        provider: OAUTH_PROVIDERS.APPLE,
      });
    }
  };

  if (IS_RN_WEBVIEW) {
    return (
      <AppleButtonUI
        handleClick={handleClick}
        loading={loading}
        errorMessage={errorMessage as React.ReactChild}
        provider={provider}
        disableButton={disableButton}
      />
    );
  }

  return (
    <AppleSignin
      authOptions={{
        clientId: APPLE_OAUTH_CLIENT_ID as string,
        scope: 'name email',
        usePopup: true,
        redirectURI,
      }}
      uiType="light"
      onSuccess={handleSuccess}
      onError={noop}
      skipScript={false}
      render={props => (
        <AppleButtonUI
          handleClick={handleClick}
          loading={loading}
          errorMessage={errorMessage as React.ReactChild}
          provider={provider}
          disableButton={disableButton}
          onClickAppleButton={props.onClick}
        />
      )}
    />
  );
};

AppleButton.displayName = 'AppleButton';

export default AppleButton;
